<template>
  <div :class="$style.signupContainer">
    <div :class="$style.header">
      <h2 :class="$style.title">회원가입</h2>
      <p :class="$style.subtitle">링꾹에 오신 것을 환영합니다!</p>
    </div>

    <form @submit.prevent="handleSignup" :class="$style.signupForm" novalidate>
      <!-- 이메일 입력 -->
      <div :class="$style.inputGroup">
        <label :class="$style.label" for="email">이메일</label>
        <input id="email" v-model="formData.email" type="email"
          :class="[$style.input, { [$style.error]: errors.email }]" placeholder="이메일을 입력해주세요" autocomplete="email" />
        <span v-if="errors.email" :class="$style.errorText">{{
          errors.email
        }}</span>
      </div>

      <!-- 비밀번호 입력 -->
      <div :class="$style.inputGroup">
        <label :class="$style.label" for="password">비밀번호</label>
        <input id="password" v-model="formData.password" type="password"
          :class="[$style.input, { [$style.error]: errors.password }]" placeholder="비밀번호를 입력해주세요"
          autocomplete="new-password" />
        <span v-if="errors.password" :class="$style.errorText">{{
          errors.password
        }}</span>
      </div>

      <!-- 비밀번호 확인 -->
      <div :class="$style.inputGroup">
        <label :class="$style.label" for="confirmPassword">비밀번호 확인</label>
        <input id="confirmPassword" v-model="formData.confirmPassword" type="password"
          :class="[$style.input, { [$style.error]: errors.confirmPassword }]" placeholder="비밀번호를 다시 입력해주세요"
          autocomplete="new-password" />
        <span v-if="errors.confirmPassword" :class="$style.errorText">{{
          errors.confirmPassword
        }}</span>
      </div>

      <!-- 약관 동의 -->
      <div :class="$style.checkboxGroup">
        <label :class="$style.checkboxLabel">
          <input v-model="formData.agreeTerms" type="checkbox" :class="$style.checkbox" required />
          <span :class="$style.checkboxText">
            <a href="#" @click.prevent="openTermsModal" :class="$style.link">이용약관</a>
            및
            <a href="#" @click.prevent="openPrivacyModal" :class="$style.link">개인정보처리방침</a>에 동의
          </span>
        </label>
        <span v-if="errors.agreeTerms" :class="$style.errorText">{{
          errors.agreeTerms
        }}</span>
      </div>

      <!-- 버튼 그룹 -->
      <div :class="$style.buttonGroup">
        <button type="button" @click="$emit('cancel')" :class="[$style.button, $style.cancelButton]">
          취소
        </button>
        <button type="submit" :class="[$style.button, $style.submitButton]" :disabled="isLoading">
          {{ isLoading ? "가입 중..." : "회원가입" }}
        </button>
      </div>
    </form>

    <!-- 소셜 로그인 구분선 -->
    <div :class="$style.divider">
      <span :class="$style.dividerText">또는</span>
    </div>

    <!-- 소셜 로그인 버튼들 -->
    <div :class="$style.socialButtons">
      <button :class="[$style.socialButton, $style.kakaoButton]" @click="kakaoSignup">
        <img :class="$style.socialIcon" src="@/assets/images/kakao_fill.png" alt="kakao icon" />
        <span :class="$style.socialText">카카오로 계속하기</span>
      </button>
    </div>

    <!-- 로그인 링크 -->
    <div :class="$style.loginLink">
      <span :class="$style.loginText">이미 계정이 있으신가요?</span>
      <button type="button" @click="$emit('switchToLogin')" :class="$style.linkButton">
        로그인하기
      </button>
    </div>
  </div>

  <!-- 개인정보처리방침 모달 -->
  <privacy-modal :is-visible="showPrivacy" @close="showPrivacy = false" />

  <!-- 이용약관 모달 -->
  <terms-modal :is-visible="showTerms" @close="showTerms = false" />
</template>

<script>
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "/firebaseConfig";
import { joinUser, handleLogin } from "@/utils/write/user";
import { trackLoginActivity, getDeviceInfo } from "@/utils/activityTracker";
import PrivacyModal from "@/components/modal/PrivacyModal.vue";
import TermsModal from "@/components/TermsModal.vue";
import { TERMS_NOTICE_VERSION } from "@/utils/termsNotice";

export default {
  name: "SignupForm",
  components: {
    PrivacyModal,
    TermsModal,
  },
  emits: ["cancel", "switchToLogin", "success"],
  data() {
    return {
      isLoading: false,
      formData: {
        email: "",
        password: "",
        confirmPassword: "",
        agreeTerms: false,
      },
      errors: {},
      showPrivacy: false,
      showTerms: false,
    };
  },
  computed: {
    nickname() {
      if (!this.formData.email) return "";
      const emailPrefix = this.formData.email.split("@")[0];
      return emailPrefix;
    },
  },
  methods: {
    validateForm() {
      this.errors = {};

      // 이메일 검증
      if (!this.formData.email) {
        this.errors.email = "이메일을 입력해주세요.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)) {
        this.errors.email = "올바른 이메일 형식을 입력해주세요.";
      } else if (this.formData.email.length > 254) {
        this.errors.email = "이메일이 너무 깁니다.";
      }

      // 비밀번호 검증
      if (!this.formData.password) {
        this.errors.password = "비밀번호를 입력해주세요.";
      } else if (this.formData.password.length < 6) {
        this.errors.password = "비밀번호는 6자 이상이어야 합니다.";
      } else if (this.formData.password.length > 128) {
        this.errors.password = "비밀번호가 너무 깁니다.";
      } else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(this.formData.password)) {
        this.errors.password = "비밀번호는 영문과 숫자를 포함해야 합니다.";
      }

      // 비밀번호 확인 검증
      if (!this.formData.confirmPassword) {
        this.errors.confirmPassword = "비밀번호 확인을 입력해주세요.";
      } else if (this.formData.password !== this.formData.confirmPassword) {
        this.errors.confirmPassword = "비밀번호가 일치하지 않습니다.";
      }

      // 약관 동의 검증
      if (!this.formData.agreeTerms) {
        this.errors.agreeTerms = "이용약관에 동의해주세요.";
      }

      return Object.keys(this.errors).length === 0;
    },

    async handleSignup() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;

      try {
        const confirmedAt = new Date().toISOString();

        // 백엔드에서 Firebase Auth + Firestore 생성 및 Custom Token 발급
        const result = await joinUser(
          this.formData.email,
          this.nickname,
          {
            password: this.formData.password,
            termsNoticeConfirmedVersion: TERMS_NOTICE_VERSION,
            termsNoticeConfirmedAt: confirmedAt,
          }
        );

        // Custom Token으로 자동 로그인
        try {
          await signInWithCustomToken(auth, result.customToken);
        } catch (signInError) {
          console.error("로그인 실패:", signInError);
          this.$alert(
            "회원가입은 완료되었습니다. 로그인 페이지에서 로그인해주세요.",
            "회원가입 완료"
          );
          this.$emit("switchToLogin");
          return;
        }

        // 로그인 처리 (loginHistory 기록 포함)
        try {
          const deviceInfo = getDeviceInfo();

          // ID Token 획득
          const idToken = await auth.currentUser.getIdToken();

          await handleLogin(this.formData.email, "email_signup", deviceInfo, idToken);

          // 로그인 활동 기록
          await trackLoginActivity("email_signup");
        } catch (error) {
          console.error("로그인 처리 실패:", error);
        }

        // 회원가입 완료 메시지
        this.$alert(
          `회원가입이 완료되었습니다!\n\n환영합니다, ${this.formData.email}님!`,
          "회원가입 완료"
        );

        // 성공 이벤트 발생
        this.$emit("success", {
          email: result.email,
          uid: result.uid,
          nickname: result.nickname,
          auth: null,
          artistLikes: [],
          liveLikes: [],
          firebaseAuthEnabled: true,
          isBlocked: false,
        });
      } catch (error) {
        console.error("회원가입 오류:", error);

        // 백엔드 에러 응답 처리
        const errorCode = error.response?.data?.code;
        const errorMessage = error.response?.data?.message;

        if (errorCode === "auth/email-already-in-use") {
          this.errors.email = "이미 사용 중인 이메일입니다.";
        } else if (errorCode === "auth/invalid-email") {
          this.errors.email = "유효하지 않은 이메일 형식입니다.";
        } else if (errorCode === "auth/weak-password") {
          this.errors.password = "비밀번호가 너무 약합니다. 6자 이상 입력해주세요.";
        } else {
          this.$alert(
            errorMessage || "회원가입 중 오류가 발생했습니다. 다시 시도해주세요.",
            "오류"
          );
        }
      } finally {
        this.isLoading = false;
      }
    },

    kakaoSignup() {
      // 카카오 로그인 (자동 회원가입)
      window.Kakao.Auth.authorize({
        redirectUri: process.env.VUE_APP_BASE_URL + "/kakaoLogin",
        throughTalk: false,
        scope: 'profile_nickname,account_email',
      });
    },

    openTermsModal() {
      // 이용약관 모달 열기
      // console.log("이용약관 모달 열기 시도");
      this.showTerms = true;
      // console.log("showTerms 상태:", this.showTerms);
    },

    openPrivacyModal() {
      // 개인정보처리방침 모달 열기
      // console.log("개인정보처리방침 모달 열기 시도");
      this.showPrivacy = true;
      // console.log("showPrivacy 상태:", this.showPrivacy);
    },
  },
};
</script>

<style module>
.signupContainer {
  max-width: 400px;
  margin: 0 auto;
  padding: 30px 20px;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.title {
  font-size: 1.8rem;
  font-weight: bold;
  color: #333;
  margin: 0 0 10px 0;
}

.subtitle {
  font-size: 1rem;
  color: #666;
  margin: 0;
}

.signupForm {
  margin-bottom: 30px;
}

.inputGroup {
  margin-bottom: 20px;
}

.label {
  display: block;
  text-align: left;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
}

.input[type="text"],
.input[type="password"],
.input[type="email"] {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.input:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.1);
}

.input.error {
  border-color: #ff6b6b;
}

.errorText {
  display: block;
  margin-top: 5px;
  font-size: 0.8rem;
  color: #ff6b6b;
}

.checkboxGroup {
  margin-bottom: 25px;
}

.checkboxLabel {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
}

.checkbox {
  margin-top: 2px;
  width: 12px;
  height: 12px !important;
  accent-color: #66bdff;
}

.checkboxText {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
}

.link {
  color: #66bdff;
  text-decoration: underline;
}

.link:hover {
  color: #4a9eff;
}

.buttonGroup {
  display: flex;
  gap: 12px;
  margin-bottom: 30px;
}

.button {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.cancelButton {
  background: #f5f5f5;
  color: #666;
}

.cancelButton:hover {
  background: #e8e8e8;
}

.submitButton {
  background: #66bdff;
  color: white;
}

.submitButton:hover:not(:disabled) {
  background: #4a9eff;
  transform: translateY(-1px);
}

.submitButton:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.divider {
  position: relative;
  text-align: center;
  margin: 30px 0;
}

.divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e0e0e0;
}

.dividerText {
  background: white;
  padding: 0 5px;
  color: #999;
  font-size: 0.9rem;
  position: relative;
  z-index: 1;
}

.socialButtons {
  margin-bottom: 25px;
}

.socialButton {
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fee502;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.socialButton:hover {
  background: #fdd835;
}

.socialIcon {
  width: 20px;
  height: 20px;
}

.socialText {
  font-size: 1rem;
}

.loginLink {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.loginText {
  font-size: 0.9rem;
  color: #666;
  margin-right: 8px;
}

.linkButton {
  background: none;
  border: none;
  color: #66bdff;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
}

.linkButton:hover {
  color: #4a9eff;
}

/* 모바일 반응형 */
@media (max-width: 480px) {
  .signupContainer {
    padding: 20px 15px;
  }

  .title {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 0.9rem;
  }

  .input {
    padding: 10px 14px;
  }

  .button {
    padding: 10px 16px;
    font-size: 0.9rem;
  }

  .socialButton {
    padding: 10px 14px;
    font-size: 0.9rem;
  }

  .socialIcon {
    width: 18px;
    height: 18px;
  }
}
</style>
